// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ProductDetailsQuantityOrderedStyles } from "@insite/content-library/Widgets/ProductDetails/ProductDetailsQuantityOrdered";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductPriceAndActions, {
    ProductPriceAndActionsStyle,
} from "@pacific/App/Components/Product/ProductPriceAndActions";
import ProductDetailsSummary from "@pacific/App/Components/ProductDetails/ProductDetailsSummary";
import { xIsAuthenticated } from "@pacific/App/Store/Selectors/GeneralSelectors";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProductContext;

export interface ProductDetailsHeaderContentStyles {
    container?: InjectableCss;
    detailsSection?: InjectableCss;
    addToCartSection?: InjectableCss;
    productPartNumbers?: ProductPartNumbersStyles;
    productPrice?: ProductPriceStyles;
    qtyOrdered?: ProductDetailsQuantityOrderedStyles;
    productPriceAndActionsStyle?: ProductPriceAndActionsStyle;
}

export const productDetailsHeaderContentStyles: ProductDetailsHeaderContentStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
        `,
    },
    detailsSection: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    addToCartSection: {
        css: css`
            display: flex;
            align-items: center;
            flex-direction: row;
        `,
    },
    productPartNumbers: {
        container: {
            css: css`
                display: flex;
                align-items: center;
            `,
        },
    },
    productPrice: {
        wrapper: {
            css: css`
                align-items: flex-end;
            `,
        },
    },
    qtyOrdered: {
        quantityOrdered: {
            cssOverrides: {
                formField: css`
                    flex: 0 0 50px;
                    height: 32px;
                    margin: 0 15px 0 25px;
                `,
                inputSelect: css`
                    height: 32px;
                    width: 100%;
                    text-align: center;
                `,
            },
        },
    },
    productPriceAndActionsStyle: {
        productGridContainer: {
            container: {
                css: css`
                    max-width: none;
                `,
            },
            priceAndActions: {
                css: css`
                    background-color: transparent;
                    padding: 0;
                    flex-direction: row;
                    flex-wrap: wrap;
                `,
            },
            price: {
                css: css`
                    flex: 0 0 100%;
                    text-align: right;
                `,
            },
            availability: {
                css: css`
                    margin: 0;
                    flex: 1;
                `,
            },
            addToCartActions: {
                css: css`
                    margin-bottom: 0;
                    flex: 0 0 auto;
                `,
            },
        },
    },
};

const styles = productDetailsHeaderContentStyles;

const ProductDetailsHeaderContent: FC<Props> = ({ productContext }) => {
    const { product } = productContext;
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.detailsSection}>
                <ProductDetailsSummary product={product} />
            </StyledWrapper>
            <StyledWrapper {...styles.addToCartSection}>
                <ProductPriceAndActions
                    showAddToList={false}
                    iconOnly={true}
                    extendedStyles={styles.productPriceAndActionsStyle}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(ProductDetailsHeaderContent),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
